/**
 * Custom hook for MetadataInspector state management
 *
 * Manages entity list fetching, entity selection, and detail fetching
 * using React Query for caching and automatic background updates.
 *
 * Architecture:
 * 1. GET /metadata/entities - Returns list of API entities with name, path, icon
 * 2. GET /{entity.path}/metadata - Returns full metadata with lifecycle, access, etc.
 */

import { useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { metadataService, type ApiEntity } from '@/services/metadataService'
import { apiService } from '@/services/apiService'
import type { SchemaMetadata } from '../types'

/**
 * Hook return type
 */
export interface UseMetadataInspectorReturn {
  // Entity list
  entities: ApiEntity[]
  entitiesLoading: boolean
  entitiesError: string | null

  // Selection
  selectedEntity: ApiEntity | null
  selectEntity: (entity: ApiEntity | null) => void

  // Search
  searchTerm: string
  setSearchTerm: (term: string) => void
  filteredEntities: ApiEntity[]

  // Selected entity metadata
  entityMetadata: SchemaMetadata | null
  metadataLoading: boolean
  metadataError: string | null

  // Refresh
  refreshEntities: () => void
  refreshMetadata: () => void
}

/**
 * Custom hook for MetadataInspector
 */
export function useMetadataInspector(): UseMetadataInspectorReturn {
  // Local state
  const [selectedEntity, setSelectedEntity] = useState<ApiEntity | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Fetch entity list from /metadata/entities
  // Dev tool - always fetch fresh data to avoid stale cache after deployments
  const {
    data: entitiesData,
    isLoading: entitiesLoading,
    error: entitiesErrorObj,
    refetch: refreshEntities
  } = useQuery({
    queryKey: ['metadata', 'entities'],
    queryFn: async () => {
      const response = await metadataService.listEntities()
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch entities')
      }
      return response.data
    },
    staleTime: 0, // Always consider stale - dev tool should show fresh data
    refetchOnMount: 'always', // Refetch when component mounts
  })

  const entities = entitiesData || []
  const entitiesError = entitiesErrorObj ? (entitiesErrorObj as Error).message : null

  // Filter entities by search term (matches name or path)
  const filteredEntities = useMemo(() => {
    if (!searchTerm.trim()) {
      return entities
    }
    const term = searchTerm.toLowerCase()
    return entities.filter(entity =>
      entity.name.toLowerCase().includes(term) ||
      entity.path.toLowerCase().includes(term)
    )
  }, [entities, searchTerm])

  // Fetch selected entity's full metadata from /{entity.path}/metadata
  // Include system fields (allow_system=true) so hidden fields toggle works
  // Dev tool - always fetch fresh data to avoid stale cache after deployments
  const {
    data: entityMetadata,
    isLoading: metadataLoading,
    error: metadataErrorObj,
    refetch: refreshMetadata
  } = useQuery({
    queryKey: ['metadata', 'entity-detail', selectedEntity?.path],
    queryFn: async () => {
      if (!selectedEntity) {
        throw new Error('No entity selected')
      }

      // Fetch from /{entity.path}/metadata (or /metadata-read for views) with allow_system=true
      // This includes ALL fields so the UI can filter client-side based on toggle
      const normalizedPath = selectedEntity.path.startsWith('/')
        ? selectedEntity.path.replace(/\/$/, '')
        : `/${selectedEntity.path}`.replace(/\/$/, '')

      // Views use /metadata-read endpoint (served by crud_router's read_service_cls)
      const metadataEndpoint = selectedEntity.isView ? 'metadata-read' : 'metadata'

      const response = await apiService.get<SchemaMetadata>(
        `${normalizedPath}/${metadataEndpoint}?allow_system=true`,
        { suppressErrors: true }
      )

      if (!response.success || !response.data) {
        throw new Error(response.message || `Failed to fetch metadata for ${selectedEntity.name}`)
      }

      return response.data as SchemaMetadata
    },
    enabled: !!selectedEntity,
    staleTime: 0, // Always consider stale - dev tool should show fresh data
    refetchOnMount: 'always', // Refetch when entity changes
  })

  const metadataError = metadataErrorObj ? (metadataErrorObj as Error).message : null

  // Selection handler
  const selectEntity = useCallback((entity: ApiEntity | null) => {
    setSelectedEntity(entity)
  }, [])

  return {
    entities,
    entitiesLoading,
    entitiesError,
    selectedEntity,
    selectEntity,
    searchTerm,
    setSearchTerm,
    filteredEntities,
    entityMetadata: entityMetadata || null,
    metadataLoading,
    metadataError,
    refreshEntities: () => refreshEntities(),
    refreshMetadata: () => refreshMetadata()
  }
}
